import { Button } from "components/DesignSystem/Button/Button";
import { CheckboxField } from "components/DesignSystem/Checkbox/CheckboxField";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { SelectAutofillCombobox } from "components/SelectAutofillCombobox";
import { PersonaModalProps } from "components/AutoFill/PersonaModal";
import { Form, Formik, FormikValues, useFormikContext } from "formik";
import { useGetCountriesQuery } from "store/apis/autofill";
import {
  findOptionInGroups,
  getGroupedCountryOptionsByName,
} from "utils/stateRequired";

const US_COUNTRY_NAME = "United States";

const splitFullName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { first_name: "", last_name: "" };
  }
  if (parts.length === 1) {
    return { first_name: parts[0], last_name: "" };
  }
  return {
    first_name: parts.slice(0, -1).join(" "),
    last_name: parts[parts.length - 1],
  };
};

const TaxResidencyField = () => {
  const { values } = useFormikContext<FormikValues>();
  const { data: countries = [] } = useGetCountriesQuery({});

  if (values.sameAsCountryOfResidence) {
    return null;
  }

  const options = getGroupedCountryOptionsByName(countries);

  return (
    <Combobox
      label="Tax residency"
      tooltipText="This is the country where you file taxes in"
      placeholder="Select country"
      withForm
      block
      name="citizenship"
      options={options}
      value={findOptionInGroups(options, values.citizenship)}
      menuPortalTarget={document.body}
    />
  );
};

const SsnField = () => {
  const { values } = useFormikContext<FormikValues>();
  if (values.country_of_residence !== US_COUNTRY_NAME) {
    return null;
  }
  return <TextInput block type="password" label="SSN" name="tax_id" />;
};

export const PersonaDetailsModal = ({
  isLoading,
  open,
  onClose,
  initialValues,
  onSubmit,
  entityId,
}: PersonaModalProps) => {
  const { id } = initialValues;
  const { data: countries = [] } = useGetCountriesQuery({});

  const localInitialValues = id
    ? {
        id,
        full_name: `${initialValues.first_name || ""} ${
          initialValues.last_name || ""
        }`.trim(),
        email: initialValues.email || "",
        phone_number: initialValues.phone_number || "",
        country_of_residence: initialValues.country_of_residence || "",
        sameAsCountryOfResidence: Boolean(
          !initialValues.citizenship ||
            initialValues.citizenship === initialValues.country_of_residence
        ),
        citizenship: initialValues.citizenship || "",
        tax_id: initialValues.tax_id || "",
        address_id: initialValues.address_id || null,
      }
    : {
        full_name: "",
        email: "",
        phone_number: "",
        country_of_residence: "",
        sameAsCountryOfResidence: true,
        citizenship: "",
        tax_id: "",
        address_id: null,
      };

  const validate = (values: FormikValues) => {
    const errors: { full_name?: string } = {};
    if (!values.full_name.trim()) {
      errors.full_name = "Full name is required";
    }
    return errors;
  };

  const handleSubmit = (values: FormikValues) => {
    const { first_name, last_name } = splitFullName(values.full_name);
    onSubmit({
      ...(values.id && { id: values.id }),
      first_name,
      last_name,
      email: values.email,
      phone_number: values.phone_number,
      country_of_residence: values.country_of_residence,
      citizenship: values.sameAsCountryOfResidence
        ? values.country_of_residence
        : values.citizenship,
      tax_id: values.tax_id,
      address_id: values.address_id,
    });
  };

  return (
    <Modal.Root onOpenChange={onClose} open={open}>
      {open && (
        <Formik
          onSubmit={handleSubmit}
          initialValues={localInitialValues}
          validate={validate}
        >
          {({ submitForm, setFieldValue, values }) => {
            return (
              <Modal.Content asChild>
                <Form>
                  <Modal.Header>
                    <Modal.Title>{id ? "Edit" : "Add"} person</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-flex t-flex-col t-gap-4">
                    <TextInput
                      block
                      label="Full Name"
                      name="full_name"
                      placeholder="John Smith"
                      required
                    />
                    <div className="t-flex t-gap-4">
                      <TextInput
                        block
                        label="Email address"
                        name="email"
                        placeholder="name@company.com"
                      />
                      <MobileInput
                        customSize="regular"
                        name="phone_number"
                        label="Phone number"
                      />
                    </div>
                    <Combobox
                      label="Country of residence"
                      placeholder="Select country"
                      withForm
                      block
                      name="country_of_residence"
                      options={getGroupedCountryOptionsByName(countries)}
                      value={findOptionInGroups(
                        getGroupedCountryOptionsByName(countries),
                        values.country_of_residence
                      )}
                      menuPortalTarget={document.body}
                    />
                    <CheckboxField
                      name="sameAsCountryOfResidence"
                      checkboxProps={{
                        label:
                          "Tax residency is the same as Country of residence",
                      }}
                    />
                    <TaxResidencyField />
                    <SsnField />
                    <SelectAutofillCombobox
                      type="addresses"
                      entityId={entityId}
                      required={false}
                      name="address_id"
                      selected={values.address_id}
                      label="Residential address"
                      size="regular"
                      withForm
                      onChange={(autofillId) =>
                        setFieldValue("address_id", autofillId)
                      }
                      srollToSave
                    />
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button
                        type="reset"
                        customType="secondary"
                        size="regular"
                      >
                        Cancel
                      </Button>
                    </Modal.RawClose>
                    <Button
                      type="button"
                      onClick={submitForm}
                      customType="primary"
                      size="regular"
                      isLoading={isLoading}
                      disabled={isLoading}
                    >
                      Save
                    </Button>
                  </Modal.FooterButtonGroup>
                </Form>
              </Modal.Content>
            );
          }}
        </Formik>
      )}
    </Modal.Root>
  );
};
